import { CompatibilityInfo as CompatibilityInfoApiRest, UserOutputDTO } from '@kalyzee/kast-web-sdk/http';
import { CompatibilityInfo as CompatibilityInfoWebsocketServer } from '@kalyzee/kast-web-sdk/websocket'; 
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { logger } from '../../helpers/logger';
import { appMessage } from '../../helpers/message';
import { CompatibilityInfos } from '../../helpers/sdk';
import { getToken as getLocalStorageToken, removeToken as removeLocalStorageToken, setToken as setLocalStorageToken, TokenType } from '../../helpers/storage';
import { AuthStatus } from '../../interfaces/session';



export enum SessionMode {
  MASTER = 'master',
  SLAVE = 'slave'
}

export interface SessionState {
  user?: UserOutputDTO;
  disableGoogleLogin?: boolean;
  token: string | null,
  refreshToken: string | null,
  isRefreshingToken: boolean,
  authStatus: AuthStatus,
  screen?: {
    index?: number,
    name?: string,
  },
  mode?: SessionMode,
  compatibilityInfos: Partial<CompatibilityInfos>,
  microphone?: {
    mute?: boolean,
  }
}

const initialState: SessionState = {
  user: undefined,
  disableGoogleLogin: false,
  token: getLocalStorageToken(),
  refreshToken: getLocalStorageToken(TokenType.Refresh),
  isRefreshingToken: false,
  authStatus: getLocalStorageToken() ? AuthStatus.Loading : AuthStatus.Out,
  screen: undefined,
  compatibilityInfos: {},
};

export const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    login: (state, action: PayloadAction<{token: string, refreshToken?: string}>) => {
      setLocalStorageToken(action.payload.token, TokenType.Normal);
      state.token = action.payload.token;
      if (action.payload.refreshToken) {
        setLocalStorageToken(action.payload.refreshToken, TokenType.Refresh);
        state.refreshToken = action.payload.refreshToken
      }
      appMessage.postLogged(true);
      state.authStatus = AuthStatus.In;
    },
    setUser: (state, action: PayloadAction<UserOutputDTO>) => {
      state.user = action.payload;
    },
    setDisableGoogleLogin: (state, action: PayloadAction<boolean>) => {
      state.disableGoogleLogin = action.payload;
    },
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
    },
    setRefreshToken: (state, action: PayloadAction<string>) => {
      state.refreshToken = action.payload;
    },
    setIsRefreshingToken: (state, action: PayloadAction<boolean>) => {
      state.isRefreshingToken = action.payload;
    },
    setAuthStatus: (state, action: PayloadAction<AuthStatus>) => {
      state.authStatus = action.payload;
    },
    setScreenIndex: (state, action: PayloadAction<number>) => {
      // console.log('Action payload screen: ', action.payload);
      state.screen = {...(state.screen ?? {}), index: action.payload};
    },
    setScreenName: (state, action: PayloadAction<string>) => {
      // console.log('Action payload screen: ', action.payload);
      state.screen = {...(state.screen ?? {}), name: action.payload};
    },
    setMode: (state, action: PayloadAction<SessionState['mode']>) => {
      state.mode = action.payload;
    },
    reset: (state) => {
      state.token = getLocalStorageToken();
      state.refreshToken = getLocalStorageToken(TokenType.Refresh);
      state.isRefreshingToken = false;
      state.authStatus = getLocalStorageToken() ? AuthStatus.Loading : AuthStatus.Out;
    },
    logout: (state) => {
      logger.log("[LOGOUT] : on logout action");
      state.token = null;
      state.refreshToken = null;
      state.isRefreshingToken = false;
      state.authStatus = AuthStatus.Out;
      appMessage.postLogged(false);
      removeLocalStorageToken(TokenType.Normal);
      removeLocalStorageToken(TokenType.Refresh)
    },
    setApiRestCompatibilityInfo: (state, action: PayloadAction<CompatibilityInfoApiRest>) => {
      state.compatibilityInfos.apiRest = action.payload;
    },
    setWebsocketServerCompatibilityInfo: (state, action: PayloadAction<CompatibilityInfoWebsocketServer>) => {
      state.compatibilityInfos.websocketServer = action.payload;
    },
    setMicrophone: (state, action: PayloadAction<SessionState['microphone']>) => {
      state.microphone = action.payload;
    },
  },
});

export const {
  login,
  setUser,
  setDisableGoogleLogin,
  setToken,
  setRefreshToken,
  setIsRefreshingToken,
  setAuthStatus,
  setScreenIndex,
  setScreenName,
  setMode,
  reset,
  logout,
  setApiRestCompatibilityInfo,
  setWebsocketServerCompatibilityInfo,
  setMicrophone
} = sessionSlice.actions;

export default sessionSlice.reducer;
